import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useInView } from "framer-motion";
import { Quote, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { FadeIn, Eyebrow, SectionHeading } from "@/components/Reveal";
import NewsletterBox from "@/components/NewsletterBox";
import { useSite } from "@/context/SiteSettings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const catalogStats = (releases) => {
    const albums = releases.filter((r) => r.type === "Album");
    const albumTracks = albums.reduce((sum, r) => sum + (r.tracks || 0), 0);
    return [
        { value: albums.length, suffix: "", label: "Albums" },
        { value: releases.length - albums.length, suffix: "", label: "Singles" },
        { value: Math.floor(albumTracks / 10) * 10, suffix: "+", label: "Songs" },
    ];
};

const Counter = ({ value, suffix }) => {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-40px" });
    const [display, setDisplay] = useState(0);

    useEffect(() => {
        if (!inView) return;
        const duration = 1600;
        const start = performance.now();
        let raf;
        const tick = (now) => {
            const p = Math.min((now - start) / duration, 1);
            setDisplay(Math.round(value * (1 - Math.pow(1 - p, 3))));
            if (p < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [inView, value]);

    return (
        <span ref={ref} className="font-display text-4xl sm:text-5xl font-extrabold text-gold-gradient">
            {display}
            {suffix}
        </span>
    );
};


const About = () => {
    const { t } = useSite();
    const [stats, setStats] = useState(null);
    const bodyLines = [1, 2, 3, 4].map((i) => t(`about_body_${i}`));
    const carries = [1, 2, 3, 4].map((i) => ({ title: t(`about_card_${i}_title`), text: t(`about_card_${i}_text`) }));

    useEffect(() => {
        axios
            .get(`${API}/releases`)
            .then((res) => setStats(catalogStats(res.data)))
            .catch(() => setStats([]));
    }, []);

    return (
    <div data-testid="about-page" className="pt-16 sm:pt-[72px]">
        <section className="relative px-5 sm:px-8 pt-24 pb-28 sm:pt-28 sm:pb-36 overflow-hidden">
            <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[80vw] h-[50vh] bg-amber-500/10 blur-[130px] rounded-full pointer-events-none" />
            <div className="max-w-5xl mx-auto">
                <SectionHeading eyebrow={t("about_label")} title={t("about_headline")} />
                <FadeIn delay={0.1}>
                    <p className="mt-5 font-meta text-[11px] tracking-[0.3em] uppercase text-zinc-500">
                        {t("about_subline")}
                    </p>
                </FadeIn>

                <FadeIn delay={0.16}>
                    <div className="mt-16 sm:mt-20 flex items-start gap-5 sm:gap-7">
                        <Quote size={30} className="text-amber-400 shrink-0 mt-2" />
                        <p
                            data-testid="about-pull-quote"
                            className="font-display text-2xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-[1.12] max-w-3xl"
                        >
                            {t("about_quote")}
                        </p>
                    </div>
                </FadeIn>

                <div className="mt-16 sm:mt-20 max-w-3xl space-y-7 sm:space-y-8">
                    {bodyLines.map((line, i) => (
                        <FadeIn key={i} delay={0.08 * i}>
                            <p data-testid={`about-body-line-${i + 1}`} className="text-base sm:text-lg text-zinc-300 leading-relaxed">
                                {line}
                            </p>
                        </FadeIn>
                    ))}
                </div>
            </div>
        </section>

        <section data-testid="about-carries-section" className="px-5 sm:px-8 pb-28 sm:pb-36">
            <div className="max-w-5xl mx-auto">
                <FadeIn><Eyebrow>What the Music Carries</Eyebrow></FadeIn>
                <div className="mt-10 grid gap-10 sm:grid-cols-2">
                    {carries.map((c, i) => (
                        <FadeIn key={c.title} delay={i * 0.08}>
                            <div data-testid={`about-carries-card-${i + 1}`} className="border-t border-amber-500/25 pt-6">
                                <h3 className="font-display text-lg font-bold text-white">{c.title}</h3>
                                <p className="text-sm sm:text-base text-zinc-400 mt-3 leading-relaxed max-w-md">{c.text}</p>
                            </div>
                        </FadeIn>
                    ))}
                </div>
            </div>
        </section>

        <section data-testid="about-vision-section" className="px-5 sm:px-8 pb-28 sm:pb-36">
            <div className="max-w-5xl mx-auto">
                <FadeIn><Eyebrow>The Vision</Eyebrow></FadeIn>
                <FadeIn delay={0.1}>
                    <p
                        data-testid="about-vision-line"
                        className="mt-8 font-display text-xl sm:text-3xl font-bold text-white leading-snug max-w-3xl"
                    >
                        {t("about_vision")}
                    </p>
                </FadeIn>
            </div>
        </section>

        <section data-testid="stats-section" className="border-y border-amber-500/15 bg-[#0D0D13] px-5 sm:px-8 py-16">
            <div className="max-w-5xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-10 text-center">
                {(stats || []).map((s, i) => (
                    <FadeIn key={s.label} delay={i * 0.12}>
                        <Counter value={s.value} suffix={s.suffix} />
                        <p className="font-meta text-[11px] tracking-[0.3em] uppercase text-zinc-500 mt-3">{s.label}</p>
                    </FadeIn>
                ))}
            </div>
        </section>

        <section className="px-5 sm:px-8 py-24">
            <div className="max-w-4xl mx-auto">
                <NewsletterBox />
                <FadeIn delay={0.15} className="mt-10 text-center">
                    <Link
                        to="/contact"
                        data-testid="about-booking-link"
                        className="inline-flex items-center gap-2 text-amber-300 font-meta text-[11px] tracking-[0.25em] uppercase hover:gap-4 transition-all duration-300"
                    >
                        Book ayevibemuzik for your event <ArrowRight size={15} />
                    </Link>
                </FadeIn>
            </div>
        </section>
    </div>
    );
};

export default About;
